import React, { FC, useCallback, useState } from "react";
import { useTheme } from "styled-components";
import { changeProps, returnFound } from "find-and";
import { useTranslation } from "react-i18next";
import {
  ActionsGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  ITheme,
  RaisedButton,
} from "@evergis/ui";
import { ProjectContentItemDc } from "@evergis/api";
import { Color } from "@evergis/color";

import { AliasColorRow } from "./styled";
import { ColorPicker } from "../../SymbolEditor/components/ColorPicker";
import { useDialogInfo, useDialogReset } from "../../../hooks/useDialogs";
import { useProjectInfo, useProjectUpdate } from "../../../ducks/project/hooks";
import { getResourceName } from "../../../utils/resourceName";

export const LayerFolder: FC = () => {
  const theme = useTheme() as ITheme;
  const { t } = useTranslation("dialogs");
  const [{ content }] = useProjectInfo();
  const mutation = useProjectUpdate();
  const resetDialog = useDialogReset();

  const {
    resources: [resourceName, resourceAlias],
    options: { parentName, color: colorProp },
  } = useDialogInfo<string[], { parentName?: string; color?: string }>();

  const [alias, setAlias] = useState(resourceAlias || t("layerFolder.group"));
  const [color, setColor] = useState<Partial<Color> | string>(colorProp || theme.palette.primary);

  const createNewGroup = useCallback(
    (newAlias: string, newColor: Partial<Color>) => ({
      name: getResourceName(),
      alias: newAlias,
      isExpanded: true,
      children: [],
      clientData: { color: newColor },
    }),
    [],
  );

  const onSubmit = useCallback(() => {
    let items: ProjectContentItemDc[];
    const newColor = color.toString();
    const newGroup = createNewGroup(alias, newColor);

    if (parentName) {
      const parent = returnFound(content.items, { name: parentName });

      items = changeProps(content.items, { name: parentName }, { children: [...parent.children, newGroup] });
    } else if (resourceName) {
      items = changeProps(content.items, { name: resourceName }, { alias, clientData: { color: newColor } });
    } else {
      items = [...(content.items || []), newGroup];
    }

    mutation({ content: { items } });
    resetDialog();
  }, [alias, color, content?.items, createNewGroup, mutation, parentName, resetDialog, resourceName]);

  return (
    <Dialog isOpen maxWidth={"39rem"} onCloseRequest={resetDialog} hasClickAway={false}>
      <DialogTitle>{resourceName ? t("actions.edit", { ns: "common" }) : t("layerFolder.newGroup")}</DialogTitle>
      <DialogContent>
        <AliasColorRow>
          <Input
            autoFocus
            width="29rem"
            label={t("layerFolder.alias")}
            value={alias}
            onChange={e => setAlias(e.target.value)}
          />
          <ColorPicker value={color as string} zIndex={1000} onChange={setColor} />
        </AliasColorRow>
      </DialogContent>
      <DialogActions>
        <ActionsGroup>
          <RaisedButton onClick={onSubmit} primary>
            {resourceName ? t("actions.apply", { ns: "common" }) : t("actions.create", { ns: "common" })}
          </RaisedButton>
          <RaisedButton onClick={resetDialog}>{t("actions.cancel", { ns: "common" })}</RaisedButton>
        </ActionsGroup>
      </DialogActions>
    </Dialog>
  );
};
